import type { ReactNode } from "react"
import { useCallback, useEffect } from "react"
import { useNavigate, useParams, useRouter } from "@tanstack/react-router"
import { format, formatDistanceToNowStrict } from "date-fns"
import { IconCircleCheckFill18 as DoneIcon } from "nucleo-ui-fill-18"
import {
	IconCopyOutline18 as Copy,
	IconSquareArrowUpRightOutline18 as ExternalLink,
	IconCircleDashedOutline18 as OpenIcon,
} from "nucleo-ui-outline-18"

import { ACTIVATION_STAGES } from "@productlane/api/src/lib/reporting/activation-stage"
import { cn } from "@productlane/lib/src/browser"
import {
	Avatar,
	Badge,
	Empty,
	IconButton,
	Loader,
	ScrollArea,
	ScrollViewport,
	Tooltip,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { FEATURE_ICONS } from "@/components/iluvatar/feature-catalog"
import { WorkspaceMembersMenu } from "@/components/iluvatar/workspace-members-menu"
import {
	billingLabel,
	formatMrr,
	originLabel,
} from "@/components/iluvatar/workspace-overview-view"
import { WorkspaceStripeMenu } from "@/components/iluvatar/workspace-stripe-menu"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { DetailBreadcrumb, DetailTopBar } from "../../app/page-header"

type Overview = RouterOutputs["analytics"]["workspaceOverview"]

export function WorkspaceDetailScreen() {
	const navigate = useNavigate()
	const router = useRouter()
	const workspaceId = useParams({ strict: false }).id!
	const { session, isLoaded } = useSession()
	useEffect(() => {
		if (isLoaded && session?.userRole !== "ADMIN") {
			void navigate({ to: "/threads" })
		}
	}, [isLoaded, session?.userRole, navigate])

	const overviewQuery = api.analytics.workspaceOverview.useQuery(
		{ workspaceId },
		{ enabled: session?.userRole === "ADMIN", retry: false },
	)
	const utils = api.useUtils()
	const impersonateMutation = api.user.impersonateUser.useMutation({
		onSuccess: async () => {
			await utils.user.info.reset()
			window.location.replace("/threads")
		},
		onError: () => {
			toast.error({ title: "Impersonation failed. Try again." })
		},
	})

	const goBack = useCallback(() => {
		if (router.history.canGoBack()) router.history.back()
		else void navigate({ to: "/iluvatar", search: { tab: "customers" } })
	}, [navigate, router])

	const overview = overviewQuery.data

	return (
		<>
			<MetaInfo title={overview?.name ?? "Workspace"} />
			<DetailTopBar className="justify-between">
				<DetailBreadcrumb
					parentLabel="Admin"
					onParentClick={goBack}
					currentIcon={
						overview ? (
							<Avatar
								size="xs"
								name={overview.name || overview.domain}
								image={overview.logoUrl}
							/>
						) : undefined
					}
					currentLabel={overview?.name || overview?.domain || "Workspace"}
				/>
				{overview ? (
					<div className="flex items-center gap-1">
						<WorkspaceStripeMenu
							workspaceId={overview.id}
							stripeCustomerId={overview.stripeCustomerId}
							stripeSubscriptionId={overview.stripeSubscriptionId}
							trialDaysLeft={overview.trialDaysLeft}
						/>
						<WorkspaceMembersMenu
							workspaceId={overview.id}
							disabled={impersonateMutation.isLoading}
							onImpersonate={(userId) => impersonateMutation.mutate({ userId })}
						/>
						<Tooltip label="Copy workspace ID" shouldWrapChildren>
							<IconButton
								Icon={Copy}
								variant="ghost"
								onClick={async () => {
									await navigator.clipboard.writeText(overview.id)
									toast.success({ title: "Workspace ID copied" })
								}}
							/>
						</Tooltip>
					</div>
				) : null}
			</DetailTopBar>
			<div className="h-[calc(100dvh-3rem)] bg-white dark:bg-gray-900">
				{overviewQuery.isLoading ? (
					<div className="flex h-64 items-center justify-center">
						<Loader />
					</div>
				) : overview ? (
					<ScrollArea>
						<ScrollViewport>
							<WorkspaceOverviewBody overview={overview} />
						</ScrollViewport>
					</ScrollArea>
				) : (
					<Empty
						title="Workspace not found"
						text={
							overviewQuery.error?.message ?? "This workspace no longer exists."
						}
					/>
				)}
			</div>
		</>
	)
}

function WorkspaceOverviewBody({ overview }: { overview: Overview }) {
	const mrr = formatMrr(overview.mrr)
	return (
		<div className="mx-auto max-w-6xl px-6 pb-12 pt-6">
			<div className="flex items-center gap-4">
				<Avatar
					size="lg"
					name={overview.name || overview.domain}
					image={overview.logoUrl}
				/>
				<div className="min-w-0">
					<h1 className="truncate text-lg font-semibold">
						{overview.name || overview.domain}
					</h1>
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-tertiary">
						<span>
							{overview.customDomain ?? `${overview.domain}.productlane.com`}
						</span>
						<span>
							Created {format(new Date(overview.createdAt), "MMM d, yyyy")}
						</span>
						<Badge size="sm">{billingLabel(overview)}</Badge>
						{mrr ? <span className="tabular-nums">{mrr} MRR</span> : null}
					</div>
				</div>
			</div>

			<Section title="Funnel" className="mt-8">
				<FunnelStepper overview={overview} />
			</Section>

			<div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
				<div className="flex flex-col gap-6">
					<Section title="Activity">
						<ActivityPanel overview={overview} />
					</Section>
					<Section title="Setup checklist">
						<ul className="flex flex-col gap-2 text-sm">
							{overview.checklist.map((item) => (
								<li key={item.key} className="flex items-center gap-2">
									<StatusIcon done={item.done} />
									<span
										className={item.done ? "text-primary" : "text-tertiary"}
									>
										{item.label}
									</span>
								</li>
							))}
						</ul>
					</Section>
				</div>
				<div className="flex flex-col gap-6">
					<Section title="Features">
						<FeatureList features={overview.features} />
					</Section>
					<Section title="Signup">
						<SignupPanel overview={overview} />
					</Section>
				</div>
			</div>
		</div>
	)
}

function Section({
	title,
	className,
	children,
}: {
	title: string
	className?: string
	children: ReactNode
}) {
	return (
		<section
			className={cn(
				"rounded-lg border border-gray-200 p-4 dark:border-gray-700/80",
				className,
			)}
		>
			<h2 className="mb-3 text-xs font-medium text-tertiary">{title}</h2>
			{children}
		</section>
	)
}

function StatusIcon({ done }: { done: boolean }) {
	return done ? (
		<DoneIcon className="h-4 w-4 flex-none text-green-500" />
	) : (
		<OpenIcon className="h-4 w-4 flex-none text-gray-300 dark:text-gray-600" />
	)
}

function FunnelStepper({ overview }: { overview: Overview }) {
	return (
		<ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
			{ACTIVATION_STAGES.map((stage) => {
				const done = overview.reached[stage.key]
				const isCurrent =
					overview.currentStage === stage.key && !overview.reached[stage.key]
				return (
					<li
						key={stage.key}
						className={cn(
							"flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
							isCurrent && "bg-gray-100 font-medium dark:bg-gray-800",
						)}
					>
						<StatusIcon done={done} />
						<span
							className={done || isCurrent ? "text-primary" : "text-tertiary"}
						>
							{stage.label}
						</span>
					</li>
				)
			})}
		</ol>
	)
}

function ActivityPanel({ overview }: { overview: Overview }) {
	const { activity, agentUsage } = overview
	const usage: Array<[string, string]> = [
		["Customer threads", String(agentUsage.customerThreads)],
		["Agent answered", String(agentUsage.agentAnswered)],
		["Agent resolved", String(agentUsage.agentResolved)],
		[
			"Resolution rate",
			agentUsage.resolutionRate === null
				? "-"
				: `${agentUsage.resolutionRate}%`,
		],
	]
	const facts: Array<[string, string]> = [
		["Members", String(activity.memberCount)],
		["Contacts", String(activity.contactCount)],
		["Changelogs", String(activity.changelogCount)],
		["Articles", String(activity.articleCount)],
		["Imported", String(activity.importedArticleCount)],
		["Published", String(activity.publishedArticleCount)],
		["First customer thread", formatDate(activity.firstCustomerThreadAt)],
		["First agent reply", formatDate(activity.firstAgentReplyAt)],
		["AI resolutions", String(activity.aiResolutions)],
		["First resolution", formatDate(activity.firstResolutionAt)],
		["Last resolution", formatAgo(activity.lastResolutionAt)],
		["Last thread", formatAgo(activity.lastThreadAt)],
	]
	return (
		<div className="flex flex-col gap-4">
			<div>
				<div className="mb-1 text-xs text-tertiary">
					Agent, last {activity.windowDays} days
				</div>
				<dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
					{usage.map(([label, value]) => (
						<div key={label}>
							<dt className="text-xs text-tertiary">{label}</dt>
							<dd className="text-base font-medium tabular-nums">{value}</dd>
						</div>
					))}
				</dl>
			</div>
			<dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
				{facts.map(([label, value]) => (
					<div key={label}>
						<dt className="text-xs text-tertiary">{label}</dt>
						<dd className="tabular-nums">{value}</dd>
					</div>
				))}
			</dl>
			{activity.threadsByOrigin.length > 0 ? (
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="mr-1 text-xs text-tertiary">
						Threads, last {activity.windowDays} days
					</span>
					{activity.threadsByOrigin.map((row) => (
						<Badge key={row.origin} size="sm">
							{originLabel(row.origin)}
							<span className="tabular-nums text-tertiary">{row.count}</span>
						</Badge>
					))}
				</div>
			) : (
				<p className="text-sm text-tertiary">
					No threads in the last {activity.windowDays} days.
				</p>
			)}
		</div>
	)
}

function FeatureList({ features }: { features: Overview["features"] }) {
	const sorted = [...features].sort(
		(a, b) => Number(b.isActive) - Number(a.isActive),
	)
	return (
		<ul className="flex flex-col gap-1.5 text-sm">
			{sorted.map((feature) => {
				const Icon = FEATURE_ICONS[feature.name]
				return (
					<li key={feature.name} className="flex items-center gap-2">
						{Icon ? (
							<Icon
								className={cn(
									"h-4 w-4 flex-none",
									feature.isActive ? "text-primary" : "opacity-30 grayscale",
								)}
							/>
						) : (
							<span className="h-4 w-4 flex-none" />
						)}
						<span
							className={feature.isActive ? "text-primary" : "text-tertiary"}
						>
							{feature.name}
						</span>
					</li>
				)
			})}
		</ul>
	)
}

const CHECKIN_LABELS: Record<string, string> = {
	WAITING: "Waiting",
	QUEUED: "Queued",
	REVIEW: "Needs review",
	INVITED: "Invited",
	DISMISSED: "Dismissed",
	SKIPPED: "Skipped",
}

function SignupPanel({ overview }: { overview: Overview }) {
	const { createdBy, onboarding, checkin } = overview
	const answers: Array<[string, string | null]> = [
		["Role", onboarding.role],
		["Company size", onboarding.companySize],
		["Signup reason", onboarding.signupReason],
		["Heard via", onboarding.referralSource],
	]
	return (
		<div className="flex flex-col gap-3 text-sm">
			{createdBy ? (
				<div className="flex items-center gap-2">
					<Avatar
						size="xs"
						name={createdBy.name || createdBy.email}
						image={createdBy.imageUrl}
					/>
					<div className="min-w-0">
						<div className="truncate">{createdBy.name || createdBy.email}</div>
						<div className="truncate text-xs text-tertiary">
							{createdBy.email}
						</div>
					</div>
				</div>
			) : null}
			<dl className="flex flex-col gap-1.5">
				{answers.map(([label, value]) => (
					<div key={label} className="flex justify-between gap-3">
						<dt className="text-tertiary">{label}</dt>
						<dd className="truncate text-right">{value ?? "-"}</dd>
					</div>
				))}
				<div className="flex items-center justify-between gap-3">
					<dt className="text-tertiary">LinkedIn check-in</dt>
					<dd className="flex items-center gap-1">
						{checkin ? (
							<Badge size="sm">
								{CHECKIN_LABELS[checkin.status] ?? checkin.status}
							</Badge>
						) : (
							"-"
						)}
						{checkin?.linkedinUrl ? (
							<Tooltip label="Open LinkedIn profile" shouldWrapChildren>
								<IconButton
									Icon={ExternalLink}
									variant="ghost"
									onClick={() =>
										window.open(checkin.linkedinUrl ?? "", "_blank", "noopener")
									}
								/>
							</Tooltip>
						) : null}
					</dd>
				</div>
			</dl>
		</div>
	)
}

const formatDate = (value: Date | string | null) =>
	value ? format(new Date(value), "MMM d, yyyy") : "-"

const formatAgo = (value: Date | string | null) =>
	value ? `${formatDistanceToNowStrict(new Date(value))} ago` : "-"
